import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import Layout from '@/components/Layout/Layout'
import Logo from '../../images/logo.svg?react'
import { Bot, ClipboardCheck, ChevronDown, Briefcase, LayoutDashboard } from 'lucide-react'
import Module2Chat from './Module2Chat'
import Module2Review from './Module2Review'
import Module2Dashboard from './Module2Dashboard'
import { observer } from 'mobx-react-lite'
import { reaction } from 'mobx'
import { module2Store } from './module2Store'
import { meteringStore } from './meteringStore'
import { subscriptionStore } from './subscriptionStore'
import MinutesOfferModal from './MinutesOfferModal'
import PlansScreen from './PlansScreen'
import SidebarMinutes from './SidebarMinutes'
import { ModuleConfigProvider, DEFAULT_MODULE_CONFIG } from './ModuleConfigContext'
import type { ModuleConfig } from './ModuleConfigContext'
import { useChatStore, useReviewChatStore } from '@/stores/chatStore'
import { useTranslation } from '@/hooks/useTranslation'
import { useStores } from '@/components/StoreProvider'

function toModuleConfig(mod: (typeof module2Store.modules)[number]): ModuleConfig {
  return {
    id: mod.id,
    name: mod.name,
    coachFlowId: mod.coachFlowId ?? DEFAULT_MODULE_CONFIG.coachFlowId,
    roleplayFlowId: mod.roleplayFlowId ?? DEFAULT_MODULE_CONFIG.roleplayFlowId,
    reviewPromptPayloadId: mod.reviewPromptPayloadId ?? DEFAULT_MODULE_CONFIG.reviewPromptPayloadId,
    timerDuration: mod.timerDuration ?? DEFAULT_MODULE_CONFIG.timerDuration,
    defaultRemainingTime: mod.defaultRemainingTime ?? DEFAULT_MODULE_CONFIG.defaultRemainingTime,
    reviewPageSize: mod.reviewPageSize ?? DEFAULT_MODULE_CONFIG.reviewPageSize,
  }
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4050'

const Module2Page: React.FC = () => {
  const { t } = useTranslation()
  const { disconnectSocket } = useChatStore()
  const reviewChatStore = useReviewChatStore()
  const { userStore } = useStores()
  const [orgLogoFailed, setOrgLogoFailed] = useState(false)
  const orgId = userStore.user?.organization_id
  const orgLogoUrl = orgId ? `${API_URL}/api/organisations/${orgId}/logo` : null
  const orgName = userStore.user?.organization_name?.trim() || ''
  // Fallback when the organisation has no logo: up to two initials on the lime gradient (mock .customer-logo)
  const orgInitials = orgName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

  // Reset store synchronously on mount (before children render) so stale state from a
  // previous session doesn't cause Module2Chat to mount on a dead tab and connect to
  // microfrank. Must happen during render, not in useEffect (which runs after children).
  const didReset = useRef(false)
  if (!didReset.current) {
    didReset.current = true
    module2Store.setActiveTab('home')
    module2Store.setMode('selection')
    module2Store.setSelectedRolePlay(null)
    module2Store.selectedByModule = {}
    module2Store.setModules([])
  }

  // Desktop: open all dropdowns when modules load; mobile: keep closed, close on resize to mobile
  const syncDropdownsToViewport = () => {
    if (typeof window === 'undefined') return
    if (window.innerWidth >= 768) {
      module2Store.openAllModules()
    } else {
      module2Store.closeAllModules()
    }
  }
  useLayoutEffect(() => {
    syncDropdownsToViewport()
    window.addEventListener('resize', syncDropdownsToViewport)
    return () => window.removeEventListener('resize', syncDropdownsToViewport)
  }, [])

  // When modules load (e.g. from Module2Chat fetch): on desktop open all dropdowns
  useEffect(() => {
    const dispose = reaction(
      () => module2Store.modules.length,
      (len) => {
        if (len > 0 && typeof window !== 'undefined' && window.innerWidth >= 768) {
          module2Store.openAllModules()
        }
      },
      { fireImmediately: true },
    )
    return dispose
  }, [])

  // The subscription decides whether this page shows the workspace or the plans, and
  // feeds the minutes block in the sidebar. B2B users get `applies: false` and see
  // the page as before.
  useEffect(() => {
    subscriptionStore.refresh()
  }, [])

  // Back from Stripe (a top-up or a subscription checkout) or from the customer portal.
  // The query string is read once, on load, and stripped at once so a refresh cannot
  // replay it. A module it names is selected as soon as the module list arrives, which
  // Module2Chat fetches after mount.
  const [returnedModuleId, setReturnedModuleId] = useState<number | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const purchase = params.get('purchase')
    const subscription = params.get('subscription')
    const portal = params.get('portal')
    const buy = params.get('buy')
    const moduleId = Number(params.get('module'))
    const sessionId = params.get('session_id')
    if (!purchase && !subscription && !portal && !buy) return

    if (Number.isInteger(moduleId) && moduleId > 0) setReturnedModuleId(moduleId)

    for (const key of ['purchase', 'subscription', 'portal', 'module', 'session_id', 'buy']) params.delete(key)
    const query = params.toString()
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`)

    if (subscription === 'success' && sessionId) subscriptionStore.confirmReturn(sessionId)
    else if (subscription === 'cancelled') subscriptionStore.cancelledReturn()
    else if (purchase === 'success' && sessionId) meteringStore.confirmReturn(sessionId)
    else if (purchase === 'cancelled') meteringStore.cancelledReturn()
    else if (buy) meteringStore.openOffer()
    // Back from the portal: `refresh` above already reads what may have changed there.
  }, [])

  // The modules arrive a moment later; select the one the payment was for.
  useEffect(() => {
    if (returnedModuleId === null) return
    const dispose = reaction(
      () => module2Store.modules.map((m) => m.id).join(','),
      () => {
        if (!module2Store.modules.some((m) => m.id === returnedModuleId)) return
        module2Store.setActiveModuleId(returnedModuleId)
        setReturnedModuleId(null)
      },
      { fireImmediately: true },
    )
    return dispose
  }, [returnedModuleId])

  // Whatever opened the modal, its body is the organisation's top-ups.
  useEffect(() => {
    const dispose = reaction(
      () => meteringStore.offerOpen,
      (open) => {
        if (open) meteringStore.loadOffer()
      },
      { fireImmediately: true },
    )
    return dispose
  }, [])

  // A B2C user with under a minute on the selected module is told so: on landing, on
  // switching to such a module, and when a roleplay leaves them there. Keyed on both
  // values so that dismissing the modal does not bring it straight back; the start
  // button and a refused start reopen it themselves.
  useEffect(() => {
    const dispose = reaction(
      () => [module2Store.activeModuleId, module2Store.activeModuleIsOutOfMinutes] as const,
      ([, outOfMinutes]) => {
        if (outOfMinutes) meteringStore.openOffer()
        else meteringStore.dismissOffer()
      },
      { fireImmediately: true, equals: (a, b) => a[0] === b[0] && a[1] === b[1] },
    )
    return dispose
  }, [])

  // Disconnect when page unmounts or user leaves browser tab
  useEffect(() => {
    return () => {
      disconnectSocket()
      reviewChatStore.disconnectSocket()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Template ".module-child" look: muted by default, lime-tinted when active, soft brand hover.
  const moduleChildClasses = `w-full flex items-center justify-start gap-2 px-2.5 py-1.5 rounded-md text-[11.5px] cursor-pointer border-none outline-none transition-all duration-150
    data-[state=active]:bg-[rgba(197,242,106,0.18)] data-[state=active]:text-[var(--m2-ink)] data-[state=active]:font-medium
    data-[state=inactive]:text-[var(--m2-muted)] hover:bg-[rgba(15,76,58,0.05)] hover:text-[var(--m2-ink)]`

  function changeTab(tab: string) {
    // Disconnect existing WebSocket when switching tabs
    disconnectSocket()

    // When switching to home, reset mode to selection
    if (tab === 'home') {
      module2Store.setMode('selection')
    }

    // Parse dynamic tabs like 'chat-1', 'review-2'
    const chatMatch = tab.match(/^chat-(\d+)$/)
    const reviewMatch = tab.match(/^review-(\d+)$/)

    if (chatMatch) {
      const newModuleId = Number(chatMatch[1])
      // Restore per-module role play selection when switching modules
      if (module2Store.activeModuleId !== newModuleId) {
        module2Store.restoreSelectionForModule(newModuleId)
      }
      module2Store.setActiveModuleId(newModuleId)
      module2Store.setMode('chat')
    } else if (reviewMatch) {
      const newModuleId = Number(reviewMatch[1])
      // Restore per-module role play selection when switching modules
      if (module2Store.activeModuleId !== newModuleId) {
        module2Store.restoreSelectionForModule(newModuleId)
      }
      module2Store.setActiveModuleId(newModuleId)
    } else if (tab === 'chat') {
      module2Store.setMode('chat')
    }

    module2Store.setActiveTab(tab)
    // On mobile, close dropdown after selecting a tab
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      module2Store.closeAllModules()
    }
  }

  // A B2C user without an active subscription: the plans, and nothing else.
  if (subscriptionStore.showPaywall) {
    return (
      <Layout>
        <div className="flex flex-1 min-h-0 w-full bg-gradient-to-br from-gray-50 via-white to-gray-100" id="module2-page">
          <PlansScreen />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="flex flex-1 min-h-0 w-full flex-row justify-center bg-[#F7F9F6] overflow-hidden md:overflow-visible" id="module2-page">
        <Tabs value={module2Store.activeTab} onValueChange={changeTab} className="flex flex-1 min-h-0 flex-col md:flex-row">
          <TabsList
            className="module2-coach flex w-full md:w-56 flex-row md:flex-col h-auto justify-start items-stretch p-2 md:p-3 border-b md:border-b-0 md:border-r shrink-0 gap-1 rounded-none overflow-x-auto md:overflow-visible"
            style={{ background: 'var(--m2-card)', borderColor: 'var(--m2-line)' }}
          >
            {/* Customer block (mock .customer-block): 40px rounded logo + organisation name in a bordered card */}
            <div className="hidden md:flex items-center gap-2.5 w-full mb-4 p-3 rounded-[10px] border bg-white" style={{ borderColor: 'var(--m2-line)' }}>
              <div className="w-10 h-10 rounded-[9px] overflow-hidden shrink-0 flex items-center justify-center" style={{ background: orgLogoUrl && !orgLogoFailed ? '#fff' : undefined }}>
                {orgLogoUrl && !orgLogoFailed ? (
                  <img src={orgLogoUrl} alt="Organisation logo" className="w-full h-full object-contain" onError={() => setOrgLogoFailed(true)} />
                ) : orgInitials ? (
                  <span
                    className="w-full h-full flex items-center justify-center text-[18px] font-extrabold tracking-[-0.5px]"
                    style={{ background: 'linear-gradient(135deg, #C5F26A 0%, #A4D447 100%)', color: 'var(--m2-brand)' }}
                  >
                    {orgInitials}
                  </span>
                ) : (
                  <Logo height={36} fill="#0F4C3A" />
                )}
              </div>
              {orgName && (
                <div className="text-[13.5px] font-bold leading-[1.2] tracking-[-0.2px] min-w-0 break-words" style={{ color: 'var(--m2-ink)' }}>
                  {orgName}
                </div>
              )}
            </div>

            {/* Top-level Dashboard tab (user-wide, above the modules list) */}
            <TabsTrigger
              value="dashboard"
              className="w-full flex-none h-auto flex items-center justify-start gap-2 px-3 py-2.5 rounded-lg text-[12.5px] font-medium transition-all duration-200 shrink-0 mb-1
                data-[state=active]:bg-[var(--m2-mint)] data-[state=active]:text-[var(--m2-ink)] data-[state=active]:shadow-none
                data-[state=inactive]:text-[var(--m2-ink)] hover:bg-[rgba(15,76,58,0.05)]"
            >
              <span className="flex items-center justify-center w-[22px] h-[22px] rounded-[5px] shrink-0" style={{ background: 'var(--m2-mint)', color: 'var(--m2-brand)' }}>
                <LayoutDashboard style={{ width: 13, height: 13 }} />
              </span>
              <span>{t('main.tabs.dashboard', 'Dashboard')}</span>
            </TabsTrigger>

            {/* Nav label */}
            <div className="hidden md:block text-[9.5px] font-semibold tracking-[1.5px] px-3 mb-2 mt-1" style={{ color: 'var(--m2-muted)' }}>
              YOUR MODULES
            </div>

            {/* Dynamic Modules from API */}
            {module2Store.modules.map((mod) => {
              const isOpen = module2Store.isModuleOpen(mod.id)
              return (
                <div key={mod.id} className="w-full shrink-0">
                  {/* Module Dropdown Header */}
                  <button
                    type="button"
                    onClick={() => module2Store.toggleModuleOpen(mod.id)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-[12.5px] font-medium transition-all duration-200"
                    style={isOpen ? { background: 'var(--m2-mint)', color: 'var(--m2-ink)' } : { color: 'var(--m2-ink)' }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex items-center justify-center w-[22px] h-[22px] rounded-[5px] shrink-0" style={{ background: isOpen ? '#fff' : 'var(--m2-mint)', color: 'var(--m2-brand)' }}>
                        <Briefcase fill="none" style={{ width: 13, height: 13 }} />
                      </span>
                      <span className="truncate">{mod.name}</span>
                    </div>
                    <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--m2-muted)' }} />
                  </button>

                  {/* Module Dropdown Content */}
                  <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="pl-7 md:pl-8 py-1 flex flex-col gap-0.5">
                      {/* Chat */}
                      <TabsTrigger value={`chat-${mod.id}`} className={moduleChildClasses}>
                        <Bot fill="none" style={{ width: 15, height: 15 }} />
                        <span>{t('main.tabs.roleplay')}</span>
                      </TabsTrigger>
                      {/* Review */}
                      <TabsTrigger value={`review-${mod.id}`} className={moduleChildClasses}>
                        <ClipboardCheck fill="none" style={{ width: 15, height: 15 }} />
                        <span>{t('main.tabs.review')}</span>
                      </TabsTrigger>
                    </div>
                  </div>
                </div>
              )
            })}

            <SidebarMinutes />
          </TabsList>

          <TabsContent value="dashboard" className="flex flex-1 flex-col min-h-0 max-h-[calc(100dvh-5rem)]">
            <Module2Dashboard />
          </TabsContent>

          <TabsContent value="home" className="flex flex-1 flex-col min-h-0 max-h-[calc(100dvh-5rem)]">
            <ModuleConfigProvider value={module2Store.activeModuleConfig ? toModuleConfig(module2Store.activeModuleConfig) : DEFAULT_MODULE_CONFIG}>
              <Module2Chat />
            </ModuleConfigProvider>
          </TabsContent>

          {/* Dynamic Chat tabs for each module */}
          {module2Store.modules.map((mod) => (
            <TabsContent key={`chat-${mod.id}`} value={`chat-${mod.id}`} className="flex flex-1 flex-col min-h-0 max-h-[calc(100dvh-5rem)]">
              <ModuleConfigProvider value={toModuleConfig(mod)}>
                <Module2Chat />
              </ModuleConfigProvider>
            </TabsContent>
          ))}

          {/* Dynamic Review tabs for each module */}
          {module2Store.modules.map((mod) => (
            <TabsContent key={`review-${mod.id}`} value={`review-${mod.id}`} className="flex flex-1 flex-col min-h-0" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
              <ModuleConfigProvider value={toModuleConfig(mod)}>
                <Module2Review />
              </ModuleConfigProvider>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <MinutesOfferModal isOpen={meteringStore.offerOpen} moduleName={module2Store.activeModuleConfig?.name} onDismiss={meteringStore.dismissOffer} />
    </Layout>
  )
}

export default observer(Module2Page)
